import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  X,
  Eye,
  Loader2,
  CheckCircle2,
  AlertCircle,
  UserCheck,
  UserPlus,
} from "lucide-react";
import { Toast } from "../components/Toast";
import { ProductSelect } from "../components/ProductSelect";
import { ColorSelect } from "../components/ColorSelect";
import { CustomerSelect } from "../components/CustomerSelect";
import { AgreementProgressSection } from "../components/AgreementProgressSection";
import { ContractPreviewModal, type ContractPreviewData } from "../components/ContractPreviewModal";
import { projectSchedule } from "../lib/projectSchedule";
import { Input } from "@/shared/ui/Input";
import { MoneyInput } from "@/shared/ui/MoneyInput";
import { DatePicker } from "@/shared/ui/DatePicker";
import { Textarea } from "@/shared/ui/Textarea";
import { groupThousands } from "@/shared/lib/money";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/Select";
import {
  useAddAgreement,
  useConfirmAgreement,
  useDiscardAgreement,
  useProductOptions,
  useColorOptions,
} from "../hooks";
import {
  ImportError,
  fetchAgreementContractPreview,
  triggerDownload,
  discardAgreement,
  discardAgreementBeacon,
} from "../api";
import type {
  AddAgreementPayload,
  AgreementType,
  ManualClient,
  Merchant,
  ProductOption,
} from "../types";
import type { CustomerListItem } from "@/features/customers/types";

// ── Customer (inline-new) sub-form — mirrors ManualStep's 7 fields + validation ──
type ClientField = keyof ManualClient;

const EMPTY_CLIENT: ManualClient = {
  first_name: "",
  last_name: "",
  phone_number: "",
  pin: "",
  passport_number: "",
  birth_date: "",
  address: "",
};

const CLIENT_FIELDS: { key: ClientField; type?: "text" | "date" }[] = [
  { key: "first_name" },
  { key: "last_name" },
  { key: "phone_number" },
  { key: "pin" },
  { key: "passport_number" },
  { key: "birth_date", type: "date" },
  { key: "address" },
];

// Same rules as ManualStep.validateField — returns an i18n key or null.
function validateClientField(field: ClientField, value: string): string | null {
  const v = value.trim();
  if (!v) return "import.manualStep.validation.required";
  if (field === "phone_number" && !/^\+?\d{9,15}$/.test(v.replace(/[\s()-]/g, "")))
    return "import.manualStep.validation.phone";
  if (field === "pin" && !/^\d{14}$/.test(v)) return "import.manualStep.validation.pin";
  return null;
}

// ── Agreement fields ──────────────────────────────────────────────────────────
const AGREEMENT_TYPES: AgreementType[] = [
  "car",
  "technology",
  "building",
  "loan",
  "other",
  "service",
];

// String-as-typed agreement money/schedule fields (mirrors AgreementRowInput; name handled separately).
type AgreementFormFields = {
  name: string;
  selling_price: string;
  down_payment: string;
  monthly_payment: string;
  payment_count: string;
  paid_amount: string;
  first_payment_date: string;
  payment_day: string;
  cost_price: string;
  // Optional advance-reminder day count (Loan.notification_before). Day count, NOT money.
  notification_before: string;
  note: string;
};

const EMPTY_AGREEMENT: AgreementFormFields = {
  name: "",
  selling_price: "",
  down_payment: "",
  monthly_payment: "",
  payment_count: "",
  paid_amount: "",
  first_payment_date: "",
  payment_day: "",
  cost_price: "",
  notification_before: "",
  note: "",
};

// Money/number agreement fields rendered with inputMode=numeric (EditCell lesson: type=number breaks
// thousands separators + empty state). Dates use a native date input.
// Render order: payment_count BEFORE monthly_payment (req 0121.05.1) so the average-monthly hint
// sits directly under the monthly_payment input. payment_day is rendered separately in the schedule
// block (next to first_payment_date / notification_before), not here.
const MONEY_FIELDS: (keyof AgreementFormFields)[] = [
  "cost_price",
  "selling_price",
  "down_payment",
  "payment_count",
  "monthly_payment",
  "paid_amount",
];

// Subset of MONEY_FIELDS that are actual sums → space-grouped via MoneyInput ("10 000"). payment_count
// (instalment count) and payment_day (day of month) are counters, NOT money, so they stay plain inputs.
const GROUPED_MONEY_FIELDS = new Set<keyof AgreementFormFields>([
  "cost_price",
  "selling_price",
  "down_payment",
  "monthly_payment",
  "paid_amount",
]);

// ── "To'langan" input modes (0123) ────────────────────────────────────────────
// paid_amount can be entered as a money sum (default) or as a count of monthly instalments already
// paid. paid_amount STAYS the single source of truth (money, down_payment excluded — see
// support_agreements.py); count mode is just an alternate input that derives that money.

// Convert a paid-instalment COUNT into the money paid_amount stores. N full instalments = N × monthly,
// capped at the financed total (selling − down). When the count reaches payment_count the last
// instalment is non-uniform (task 0080), so snap to `financed` → remainder lands on exactly 0.
// Returns "" when the count or monthly is missing (nothing to compute yet).
function countToPaidAmount(
  count: string,
  monthly: string,
  selling: string,
  down: string,
  payCount: string,
): string {
  const n = Math.floor(Number(count));
  const m = Number(monthly);
  if (!n || n < 1 || !m) return "";
  const financed = Number(selling) - Number(down || "0");
  const pc = Number(payCount);
  const raw = n * Math.round(m);
  const capped = financed > 0 ? Math.min(raw, financed) : raw;
  const paid = pc && n >= pc && financed > 0 ? financed : capped;
  return String(Math.round(paid));
}

// Best-effort reverse (money → count), used only to seed the count input when switching into count mode.
function paidAmountToCount(paid: string, monthly: string): string {
  const p = Number(paid);
  const m = Number(monthly);
  if (!p || !m) return "";
  return String(Math.floor(p / m));
}

/**
 * Side drawer that adds a single agreement manually (0119). The customer is either an existing one
 * (searched + picked) or a brand-new one entered inline (ManualStep's 7 fields). The agreement fields
 * reuse ProductSelect (car/technology model) + ColorSelect (optional) + plain inputs. On save it POSTs
 * to add-agreement, shows a toast and stays open with a running tally so the operator can keep adding.
 * Closing returns to the mode picker.
 */
export function AgreementAddDrawer({
  merchant,
  onClose,
}: {
  merchant: Merchant;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  // Customer branch. Existing-customer search now lives inside <CustomerSelect>.
  const [customerMode, setCustomerMode] = useState<"existing" | "new">("existing");
  const [selectedClient, setSelectedClient] = useState<CustomerListItem | null>(null);

  const [newClient, setNewClient] = useState<ManualClient>(EMPTY_CLIENT);
  const [clientErrors, setClientErrors] = useState<Partial<Record<ClientField, string>>>({});

  // Agreement branch.
  const [type, setType] = useState<AgreementType>("car");
  const [fields, setFields] = useState<AgreementFormFields>(EMPTY_AGREEMENT);
  // additional_data overrides: color + product display name + model_type_id (set via ProductSelect).
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [agreementErrors, setAgreementErrors] = useState<Partial<Record<string, string>>>({});
  const [agreementLevelError, setAgreementLevelError] = useState<string | null>(null);

  const [toast, setToast] = useState<string | null>(null);
  const [addedCount, setAddedCount] = useState(0);
  // Contract preview ("dogovor") shown AFTER the agreement is created (the review POST) but BEFORE the
  // operator confirms. Holds the saved loan id + code, the real PDF (object URL + blob), and a read-only
  // HTML snapshot used when the PDF can't be fetched. null = closed. Confirm keeps the saved agreement;
  // cancel/edit rolls it back (discard). pdfUrl null → HTML fallback (flag off / 404).
  const [previewState, setPreviewState] = useState<{
    loanId: number;
    code: string;
    pdfUrl: string | null;
    blob: Blob | null;
    data: ContractPreviewData;
  } | null>(null);
  // PDF fetch in flight after a successful review POST (the modal isn't open yet during this window).
  const [pdfLoading, setPdfLoading] = useState(false);
  // "To'langan" input mode (0123): "amount" = money sum (default), "count" = number of monthly
  // instalments paid. paidCount holds the typed count; the money is derived (effectivePaidAmount).
  const [paidMode, setPaidMode] = useState<"amount" | "count">("amount");
  const [paidCount, setPaidCount] = useState("");

  const { mutate: addAgreementMutate, isPending: isAdding } = useAddAgreement();
  const { mutate: confirmAgreementMutate, isPending: isConfirming } = useConfirmAgreement();
  const { mutate: discardMutate, isPending: isDiscarding } = useDiscardAgreement();
  // The review button is busy while the POST runs AND while its PDF is being fetched.
  const isPending = isAdding || pdfLoading;

  // Tracks the loan created by the review step that has NOT yet been confirmed or discarded (0127).
  // A ref (not state) so the unmount cleanup below reads the latest value without re-subscribing.
  // Set when review succeeds; cleared on confirm-success and on cancel/discard.
  const pendingLoanRef = useRef<number | null>(null);

  // Revoke the object URL when it changes or the drawer unmounts (backstop — confirm/cancel also revoke).
  useEffect(() => {
    const url = previewState?.pdfUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewState?.pdfUrl]);

  // Orphan cleanup (0127, option b): if the drawer unmounts (operator navigated away / parent closed it)
  // while a reviewed-but-unconfirmed loan is still pending, discard it so no ghost agreement lingers.
  // Fire-and-forget — the component is going away, so we can't await or surface errors. The empty-deps
  // cleanup runs once at unmount and reads the ref's current value.
  useEffect(() => {
    return () => {
      const loanId = pendingLoanRef.current;
      if (loanId !== null) {
        pendingLoanRef.current = null;
        void discardAgreement(loanId).catch(() => {});
      }
    };
  }, []);

  // Best-effort cleanup for a full tab close / refresh: a normal DELETE can't finish during page
  // teardown, so beacon a keepalive fetch. Only fires while a loan is pending. Not guaranteed on a hard
  // crash — acceptable; an operator can always discard the orphan from the catalogue later.
  useEffect(() => {
    const onBeforeUnload = () => {
      const loanId = pendingLoanRef.current;
      if (loanId !== null) discardAgreementBeacon(loanId);
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const isCatalogueType = type === "car" || type === "technology";
  const productType = isCatalogueType ? (type as "car" | "technology") : null;
  const { data: productOptions = [], isLoading: isLoadingOptions } = useProductOptions(
    productType,
    true,
  );
  const { data: colorOptions = [], isLoading: colorsLoading } = useColorOptions(true);

  // Product display key per type (matches AgreementEditDrawer / backend overrides).
  const productNameKey = type === "car" ? "model" : "technology_model";
  const productIdKey = type === "car" ? "model_type_id" : "technology_model_id";

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleClientChange = useCallback(
    (field: ClientField) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewClient((prev) => ({ ...prev, [field]: e.target.value }));
      setClientErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    [],
  );

  const handleClientBlur = useCallback(
    (field: ClientField) => (e: React.FocusEvent<HTMLInputElement>) => {
      const errKey = validateClientField(field, e.target.value);
      setClientErrors((prev) => ({ ...prev, [field]: errKey ? t(errKey) : undefined }));
    },
    [t],
  );

  const handleFieldChange = useCallback(
    (field: keyof AgreementFormFields) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFields((prev) => ({ ...prev, [field]: e.target.value }));
        setAgreementErrors((prev) => ({ ...prev, [field]: undefined }));
        setAgreementLevelError(null);
      },
    [],
  );

  // Value-based variant for MoneyInput, which emits the raw digit string directly (not an event).
  const handleRawFieldChange = useCallback(
    (field: keyof AgreementFormFields) => (raw: string) => {
      setFields((prev) => ({ ...prev, [field]: raw }));
      setAgreementErrors((prev) => ({ ...prev, [field]: undefined }));
      setAgreementLevelError(null);
    },
    [],
  );

  const handleTypeChange = useCallback((value: string) => {
    setType(value as AgreementType);
    // Drop catalogue-specific overrides when leaving car/technology so stale ids aren't sent.
    setExtra((prev) => {
      const next = { ...prev };
      delete next.model;
      delete next.model_type_id;
      delete next.technology_model;
      delete next.technology_model_id;
      return next;
    });
    setFields((prev) => ({ ...prev, name: "" }));
    setAgreementErrors({});
    setAgreementLevelError(null);
  }, []);

  const handleProductSelect = useCallback(
    (opt: ProductOption) => {
      setExtra((prev) => ({
        ...prev,
        [productNameKey]: opt.name,
        [productIdKey]: String(opt.id),
      }));
      setAgreementErrors((prev) => ({ ...prev, name: undefined }));
      setAgreementLevelError(null);
    },
    [productNameKey, productIdKey],
  );

  const resetForm = useCallback(() => {
    setSelectedClient(null);
    setNewClient(EMPTY_CLIENT);
    setClientErrors({});
    setType("car");
    setFields(EMPTY_AGREEMENT);
    setPaidMode("amount");
    setPaidCount("");
    setExtra({});
    setAgreementErrors({});
    setAgreementLevelError(null);
  }, []);

  // Product canonical name = the display name for car/technology (from ProductSelect), else the typed name.
  const productName = isCatalogueType ? extra[productNameKey] ?? "" : fields.name;

  // Average monthly hint (advisory only — never mutates the input): round((selling − down) / count).
  // Hidden unless selling_price > 0 and payment_count > 0.
  const monthlyAvgHint = useMemo(() => {
    const selling = Number(fields.selling_price);
    const down = Number(fields.down_payment || "0");
    const count = Number(fields.payment_count);
    if (!selling || !count || count < 1) return null;
    const avg = Math.round((selling - down) / count);
    if (avg <= 0) return null;
    return groupThousands(String(avg));
  }, [fields.selling_price, fields.down_payment, fields.payment_count]);

  // Effective paid money (0123): in count mode it's derived from the typed instalment count + the
  // financial inputs; in amount mode it's the money field as-is. EVERY paid_amount consumer
  // (projection, payload, preview, live summary) reads this, so paid_amount stays authoritative.
  const effectivePaidAmount = useMemo(
    () =>
      paidMode === "count"
        ? countToPaidAmount(
            paidCount,
            fields.monthly_payment,
            fields.selling_price,
            fields.down_payment,
            fields.payment_count,
          )
        : fields.paid_amount,
    [
      paidMode,
      paidCount,
      fields.monthly_payment,
      fields.selling_price,
      fields.down_payment,
      fields.payment_count,
      fields.paid_amount,
    ],
  );

  // Switch the "To'langan" input mode, carrying the value across so nothing is lost (0123). Into count
  // mode: seed the count from the current money. Into amount mode: snapshot the derived money back into
  // the editable money field.
  const handlePaidModeChange = useCallback(
    (mode: "amount" | "count") => {
      if (mode === paidMode) return;
      if (mode === "count") {
        setPaidCount(paidAmountToCount(fields.paid_amount, fields.monthly_payment));
      } else {
        setFields((prev) => ({ ...prev, paid_amount: effectivePaidAmount }));
      }
      setPaidMode(mode);
    },
    [paidMode, fields.paid_amount, fields.monthly_payment, effectivePaidAmount],
  );

  // Live paid/remaining projection — reuses the SAME helper + AgreementProgressSection the info-modal
  // uses, so the in-drawer summary looks identical. null when inputs are insufficient (then hidden).
  const projected = useMemo(
    () =>
      projectSchedule({
        selling_price: fields.selling_price,
        down_payment: fields.down_payment,
        monthly_payment: fields.monthly_payment,
        payment_count: fields.payment_count,
        paid_amount: effectivePaidAmount,
        first_payment_date: fields.first_payment_date,
      }),
    [
      fields.selling_price,
      fields.down_payment,
      fields.monthly_payment,
      fields.payment_count,
      effectivePaidAmount,
      fields.first_payment_date,
    ],
  );

  // ── Validation + submit ────────────────────────────────────────────────────
  const buildPayload = useCallback((): {
    payload: AddAgreementPayload | null;
    clientErrs: Partial<Record<ClientField, string>>;
    agreementErrs: Partial<Record<string, string>>;
  } => {
    const clientErrs: Partial<Record<ClientField, string>> = {};
    const agreementErrs: Partial<Record<string, string>> = {};

    // Customer: existing (selected) or new (all 7 fields valid).
    let clientPart: Pick<AddAgreementPayload, "client_id" | "new_client"> | null = null;
    if (customerMode === "existing") {
      if (selectedClient) clientPart = { client_id: selectedClient.id };
      // No selection → handled by the agreementErrs.client flag below.
    } else {
      for (const { key } of CLIENT_FIELDS) {
        const errKey = validateClientField(key, newClient[key]);
        if (errKey) clientErrs[key] = t(errKey);
      }
      if (Object.keys(clientErrs).length === 0) clientPart = { new_client: newClient };
    }

    // Agreement blocking fields: product name (type-dependent), selling_price, monthly_payment, payment_count.
    if (!productName.trim()) agreementErrs.name = t("import.agreementAdd.validation.required");
    if (!fields.selling_price.trim())
      agreementErrs.selling_price = t("import.agreementAdd.validation.required");
    if (!fields.monthly_payment.trim())
      agreementErrs.monthly_payment = t("import.agreementAdd.validation.required");
    if (!fields.payment_count.trim())
      agreementErrs.payment_count = t("import.agreementAdd.validation.required");

    const customerMissing = !clientPart;
    if (
      customerMissing ||
      Object.keys(clientErrs).length > 0 ||
      Object.keys(agreementErrs).length > 0
    ) {
      return { payload: null, clientErrs, agreementErrs };
    }

    const payload: AddAgreementPayload = {
      ...clientPart!,
      type,
      name: productName,
      selling_price: fields.selling_price,
      down_payment: fields.down_payment,
      monthly_payment: fields.monthly_payment,
      payment_count: fields.payment_count,
      paid_amount: effectivePaidAmount,
      first_payment_date: fields.first_payment_date,
      payment_day: fields.payment_day,
      cost_price: fields.cost_price,
      note: fields.note,
      additional_data: extra,
    };
    // Advance-reminder days (OPTIONAL): blank → omit entirely so the backend keeps it null.
    // Never send 0 or an empty string (contract 0121.01/.02: int | null).
    const reminderRaw = fields.notification_before.trim();
    if (reminderRaw !== "") {
      const reminderNum = Number(reminderRaw);
      if (Number.isFinite(reminderNum)) payload.notification_before = reminderNum;
    }
    return { payload, clientErrs, agreementErrs };
  }, [customerMode, selectedClient, newClient, productName, fields, effectivePaidAmount, type, extra, t]);

  // Step 1 — validate, then POST to create the agreement and open the contract preview ("dogovor").
  // The review POST is what lets us render the REAL contract PDF; if the operator cancels we roll it
  // back (discard). On invalid input it surfaces the same inline errors as before and keeps the form open.
  const handlePreview = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setAgreementLevelError(null);
      const { payload, clientErrs, agreementErrs } = buildPayload();
      setClientErrors(clientErrs);
      setAgreementErrors(agreementErrs);
      // No customer chosen → surface a top-level customer error.
      if (!payload && customerMode === "existing" && !selectedClient) {
        setAgreementLevelError(t("import.agreementAdd.validation.customerRequired"));
      }
      if (!payload) return;

      // Read-only display snapshot — the PDF-unavailable fallback rendered inside the modal.
      const isNew = customerMode === "new";
      const data: ContractPreviewData = {
        merchantName: `${merchant.first_name} ${merchant.last_name}`.trim(),
        client: {
          name: isNew
            ? `${newClient.first_name} ${newClient.last_name}`.trim()
            : selectedClient
              ? `${selectedClient.first_name} ${selectedClient.last_name}`.trim()
              : "",
          phone: isNew ? newClient.phone_number : (selectedClient?.phone_number ?? ""),
          passport: isNew ? newClient.passport_number || undefined : undefined,
          pin: isNew ? newClient.pin || undefined : undefined,
        },
        agreement: {
          typeLabel: t(`import.agreements.loanType.${type}`),
          productName,
          color: extra.color || undefined,
          note: fields.note.trim() || undefined,
        },
        money: {
          cost_price: fields.cost_price,
          selling_price: fields.selling_price,
          down_payment: fields.down_payment,
          monthly_payment: fields.monthly_payment,
          payment_count: fields.payment_count,
          paid_amount: effectivePaidAmount,
        },
        schedule: projected
          ? {
              payments: projected.payments,
              paidAmount: effectivePaidAmount || "0",
              remainder: projected.remainder,
              startDate: fields.first_payment_date || projected.payments[0]?.payment_date,
            }
          : null,
        notification: {
          first_payment_date: fields.first_payment_date,
          payment_day: fields.payment_day,
          notification_before: fields.notification_before,
        },
      };

      // POST now to create the agreement, then fetch its real contract PDF for the preview.
      addAgreementMutate(
        { merchantId: merchant.id, payload },
        {
          onSuccess: async (created) => {
            // The loan now exists but is unconfirmed — track it for orphan cleanup (0127).
            pendingLoanRef.current = created.loan_id;
            setPdfLoading(true);
            try {
              const blob = await fetchAgreementContractPreview(created.loan_id);
              const pdfUrl = URL.createObjectURL(blob);
              setPreviewState({ loanId: created.loan_id, code: created.agreement_code, pdfUrl, blob, data });
            } catch {
              // Flag off / 404 / network — fall back to the HTML summary (like the app's pdfFailed).
              setPreviewState({
                loanId: created.loan_id,
                code: created.agreement_code,
                pdfUrl: null,
                blob: null,
                data,
              });
            } finally {
              setPdfLoading(false);
            }
          },
          onError: (err) => {
            // Keep the form open with inline errors — nothing was created.
            if (err instanceof ImportError) {
              if (err.field === "agreement") {
                setAgreementLevelError(err.detail ?? t("import.errors.generic"));
              } else if (err.field) {
                // Inline-new-client field error (phone/pin/passport).
                setCustomerMode("new");
                setClientErrors({ [err.field]: t(`import.errors.${err.code}`) });
              } else {
                setAgreementLevelError(t("import.errors.generic"));
              }
            } else {
              setAgreementLevelError(t("import.errors.generic"));
            }
          },
        },
      );
    },
    [
      buildPayload,
      customerMode,
      selectedClient,
      newClient,
      merchant,
      type,
      productName,
      extra,
      fields,
      effectivePaidAmount,
      projected,
      addAgreementMutate,
      t,
    ],
  );

  // Step 2a — Tasdiqlash (confirm): the loan was already created at review, but the merchant push + the
  // import-history "added" row were deferred (0127). Confirm fires them now via the confirm endpoint.
  // On success: clear the pending marker, revoke the PDF URL, close, toast, reset and bump the tally.
  // On error the preview stays open so the operator can retry (the loan still exists; confirm is idempotent).
  const handleConfirm = useCallback(() => {
    if (!previewState) return;
    const { loanId, code, pdfUrl } = previewState;
    confirmAgreementMutate(
      { loanId, merchantId: merchant.id },
      {
        onSuccess: () => {
          pendingLoanRef.current = null;
          if (pdfUrl) URL.revokeObjectURL(pdfUrl);
          setPreviewState(null);
          setToast(t("import.agreementAdd.successToast", { code }));
          resetForm();
          setAddedCount((c) => c + 1);
        },
        onError: () => {
          setToast(t("import.errors.generic"));
        },
      },
    );
  }, [previewState, confirmAgreementMutate, merchant.id, resetForm, t]);

  // Step 2b — Tahrirlash / X / backdrop (cancel): roll back the unconfirmed add (discard the just-created
  // Loan) and close the preview. The form keeps its values so the operator can edit and review again.
  const handleCancel = useCallback(() => {
    if (!previewState) return;
    const { loanId, pdfUrl } = previewState;
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    pendingLoanRef.current = null;
    discardMutate({ loanId, merchantId: merchant.id });
    setPreviewState(null);
  }, [previewState, discardMutate, merchant.id]);

  // Yuklab olish (download): save the previewed PDF to disk. Blob is held in previewState.
  const handleDownload = useCallback(() => {
    if (!previewState?.blob) return;
    triggerDownload(previewState.blob, `shartnoma-${previewState.code}.pdf`);
  }, [previewState]);

  const merchantName = useMemo(
    () => `${merchant.first_name} ${merchant.last_name}`.trim(),
    [merchant],
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  // Portal to document.body so the backdrop covers the whole viewport (not clipped by a
  // transformed/overflow ancestor) — same rationale as AgreementEditDrawer (0115).
  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/40"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Drawer panel — right-anchored side drawer */}
      <div className="relative z-50 flex h-full w-full max-w-xl flex-col bg-white shadow-float">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-grey-100 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold text-grey-800">
              {t("import.agreementAdd.drawerTitle")}
            </h3>
            <p className="truncate text-[12px] text-grey-400">{merchantName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-grey-400 hover:text-grey-700"
            aria-label="close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handlePreview} noValidate className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            {/* Running tally of agreements added without leaving the drawer. */}
            {addedCount > 0 && (
              <div className="mb-5 flex items-center gap-2 rounded-card bg-green-bg px-4 py-2.5 text-[13px] font-medium text-green-dark">
                <CheckCircle2 size={16} strokeWidth={2} className="shrink-0" />
                {t("import.agreementAdd.sessionCount", { count: addedCount })}
              </div>
            )}

            {/* ── Customer section ── */}
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
              {t("import.agreementAdd.customerSection")}
            </p>

            {/* Segmented toggle: existing ↔ new */}
            <div className="mb-4 grid grid-cols-2 gap-2">
              {(["existing", "new"] as const).map((m) => {
                const active = customerMode === m;
                const Icon = m === "existing" ? UserCheck : UserPlus;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      setCustomerMode(m);
                      setAgreementLevelError(null);
                    }}
                    className={`flex items-center justify-center gap-2 rounded-control border-2 px-3 py-2 text-[13px] font-medium transition ${
                      active
                        ? "border-green bg-green-bg text-green-dark"
                        : "border-grey-200 text-grey-600 hover:border-grey-300"
                    }`}
                  >
                    <Icon size={15} strokeWidth={2} />
                    {t(`import.agreementAdd.customer.${m}`)}
                  </button>
                );
              })}
            </div>

            {/* Existing customer branch — searchable Combobox (same look as Product/Color selects) */}
            {customerMode === "existing" && (
              <CustomerSelect
                value={selectedClient}
                onChange={setSelectedClient}
                placeholder={t("import.agreementAdd.customer.selectPlaceholder")}
                searchPlaceholder={t("import.agreementAdd.customer.searchPlaceholder")}
              />
            )}

            {/* New customer branch — ManualStep's 7 fields */}
            {customerMode === "new" && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {CLIENT_FIELDS.map(({ key, type: inputType = "text" }) => (
                  <div key={key} className={key === "address" ? "sm:col-span-2" : ""}>
                    <label
                      htmlFor={`new-client-${key}`}
                      className="mb-1 block text-[13px] font-medium text-grey-700"
                    >
                      {t(`import.manualStep.${key}`)}
                      <span className="ml-0.5 text-red">*</span>
                    </label>
                    {inputType === "date" ? (
                      <DatePicker
                        id={`new-client-${key}`}
                        value={newClient[key]}
                        onChange={(v) => {
                          setNewClient((prev) => ({ ...prev, [key]: v }));
                          setClientErrors((prev) => ({ ...prev, [key]: undefined }));
                        }}
                        placeholder={t(`import.manualStep.${key}Placeholder`)}
                        invalid={!!clientErrors[key]}
                      />
                    ) : (
                      <Input
                        id={`new-client-${key}`}
                        type={inputType}
                        value={newClient[key]}
                        onChange={handleClientChange(key)}
                        onBlur={handleClientBlur(key)}
                        placeholder={t(`import.manualStep.${key}Placeholder`)}
                        aria-invalid={clientErrors[key] ? true : undefined}
                        invalid={!!clientErrors[key]}
                      />
                    )}
                    {clientErrors[key] && (
                      <p className="mt-1 text-[12px] text-red">{clientErrors[key]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ── Agreement section ── */}
            <p className="mb-3 mt-6 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
              {t("import.agreementAdd.agreementSection")}
            </p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* Type */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-grey-700">
                  {t("import.agreementAdd.fields.type")}
                  <span className="ml-0.5 text-red">*</span>
                </label>
                <Select value={type} onValueChange={handleTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGREEMENT_TYPES.map((tp) => (
                      <SelectItem key={tp} value={tp}>
                        {t(`import.agreements.loanType.${tp}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Product / name — catalogue dropdown for car/technology, plain text otherwise */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-grey-700">
                  {isCatalogueType
                    ? t("import.agreementAdd.fields.model")
                    : t("import.agreementAdd.fields.name")}
                  <span className="ml-0.5 text-red">*</span>
                </label>
                {isCatalogueType ? (
                  <ProductSelect
                    options={productOptions}
                    value={productName}
                    onChange={handleProductSelect}
                    placeholder={t("import.agreementAdd.fields.modelPlaceholder")}
                    searchPlaceholder={t("import.agreementAdd.fields.modelSearch")}
                    isLoading={isLoadingOptions}
                  />
                ) : (
                  <Input
                    type="text"
                    value={fields.name}
                    onChange={handleFieldChange("name")}
                    placeholder={t("import.agreementAdd.fields.namePlaceholder")}
                    invalid={!!agreementErrors.name}
                  />
                )}
                {agreementErrors.name && (
                  <p className="mt-1 text-[12px] text-red">{agreementErrors.name}</p>
                )}
              </div>

              {/* Color — OPTIONAL ("" → "—") */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-grey-700">
                  {t("import.agreementAdd.fields.color")}
                </label>
                <ColorSelect
                  value={extra.color ?? ""}
                  options={colorOptions}
                  isLoading={colorsLoading}
                  placeholder={t("import.agreementAdd.fields.colorPlaceholder")}
                  onChange={(name) =>
                    setExtra((prev) => {
                      const next = { ...prev };
                      if (name) next.color = name;
                      else delete next.color;
                      return next;
                    })
                  }
                />
              </div>

              {/* Money / schedule fields */}
              {MONEY_FIELDS.map((key) => {
                const required = key === "selling_price";
                // "To'langan" gets a Summa ↔ To'lovlar soni mode toggle (0123). Count mode enters the
                // number of monthly instalments paid; the money it maps to is shown live below.
                if (key === "paid_amount") {
                  return (
                    <div key={key}>
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <label className="block text-[13px] font-medium text-grey-700">
                          {t("import.agreementAdd.fields.paid_amount")}
                        </label>
                        <div className="flex rounded-control border border-grey-200 p-0.5">
                          {(["amount", "count"] as const).map((m) => (
                            <button
                              key={m}
                              type="button"
                              onClick={() => handlePaidModeChange(m)}
                              className={`whitespace-nowrap rounded-[6px] px-2 py-0.5 text-[11px] font-medium transition ${
                                paidMode === m
                                  ? "bg-green-bg text-green-dark"
                                  : "text-grey-500 hover:text-grey-700"
                              }`}
                            >
                              {t(`import.agreementAdd.paidMode.${m}`)}
                            </button>
                          ))}
                        </div>
                      </div>
                      {paidMode === "amount" ? (
                        <MoneyInput
                          value={fields.paid_amount}
                          onChange={handleRawFieldChange("paid_amount")}
                          placeholder={t("import.agreementAdd.fields.paid_amountPlaceholder")}
                        />
                      ) : (
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={paidCount}
                          onChange={(e) => setPaidCount(e.target.value)}
                          placeholder={t("import.agreementAdd.paidMode.countPlaceholder")}
                        />
                      )}
                      {paidMode === "count" && (
                        <p className="mt-1 text-[12px] text-grey-400">
                          {effectivePaidAmount
                            ? t("import.agreementAdd.paidMode.moneyHint", {
                                amount: groupThousands(effectivePaidAmount),
                              })
                            : t("import.agreementAdd.paidMode.needMonthly")}
                        </p>
                      )}
                    </div>
                  );
                }
                return (
                  <div key={key}>
                    <label className="mb-1 block text-[13px] font-medium text-grey-700">
                      {t(`import.agreementAdd.fields.${key}`)}
                      {required && <span className="ml-0.5 text-red">*</span>}
                    </label>
                    {GROUPED_MONEY_FIELDS.has(key) ? (
                      <MoneyInput
                        value={fields[key]}
                        onChange={handleRawFieldChange(key)}
                        placeholder={t(`import.agreementAdd.fields.${key}Placeholder`)}
                        invalid={!!agreementErrors[key]}
                      />
                    ) : (
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={fields[key]}
                        onChange={handleFieldChange(key)}
                        placeholder={t(`import.agreementAdd.fields.${key}Placeholder`)}
                        invalid={!!agreementErrors[key]}
                      />
                    )}
                    {/* Advisory average-monthly hint sits under the monthly_payment input only. */}
                    {key === "monthly_payment" && monthlyAvgHint && (
                      <p className="mt-1 text-[12px] text-grey-400">
                        {t("import.agreementAdd.fields.monthlyAvgHint", { amount: monthlyAvgHint })}
                      </p>
                    )}
                    {agreementErrors[key] && (
                      <p className="mt-1 text-[12px] text-red">{agreementErrors[key]}</p>
                    )}
                  </div>
                );
              })}

              {/* First payment date — shadcn calendar (DatePicker, ISO string) */}
              <div>
                <label
                  htmlFor="agreement-first-payment-date"
                  className="mb-1 block text-[13px] font-medium text-grey-700"
                >
                  {t("import.agreementAdd.fields.first_payment_date")}
                </label>
                <DatePicker
                  id="agreement-first-payment-date"
                  value={fields.first_payment_date}
                  onChange={(v) => handleRawFieldChange("first_payment_date")(v)}
                  placeholder={t("import.agreementAdd.fields.first_payment_datePlaceholder")}
                />
              </div>

              {/* Payment day — day-of-month number (1-31), NOT a date → plain numeric input */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-grey-700">
                  {t("import.agreementAdd.fields.payment_day")}
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={fields.payment_day}
                  onChange={handleFieldChange("payment_day")}
                  placeholder={t("import.agreementAdd.fields.payment_dayPlaceholder")}
                  invalid={!!agreementErrors.payment_day}
                />
                {agreementErrors.payment_day && (
                  <p className="mt-1 text-[12px] text-red">{agreementErrors.payment_day}</p>
                )}
              </div>

              {/* Advance reminder (notification_before) — OPTIONAL day count, plain numeric input */}
              <div>
                <label
                  htmlFor="agreement-notification-before"
                  className="mb-1 block text-[13px] font-medium text-grey-700"
                >
                  {t("import.agreementAdd.fields.notification_before")}
                </label>
                <Input
                  id="agreement-notification-before"
                  type="text"
                  inputMode="numeric"
                  value={fields.notification_before}
                  onChange={handleFieldChange("notification_before")}
                  placeholder={t("import.agreementAdd.fields.notification_beforePlaceholder")}
                />
              </div>

              {/* Live paid / remaining summary — same component + formula as the info-modal (req .3).
                  Hidden until projectSchedule has enough inputs. Placed ABOVE the note field. */}
              {projected && (
                <div className="sm:col-span-2">
                  <AgreementProgressSection
                    payments={projected.payments}
                    paidAmount={effectivePaidAmount || "0"}
                    remainder={projected.remainder}
                    startDate={fields.first_payment_date || projected.payments[0]?.payment_date}
                  />
                </div>
              )}

              {/* Note — full width */}
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[13px] font-medium text-grey-700">
                  {t("import.agreementAdd.fields.note")}
                </label>
                <Textarea
                  value={fields.note}
                  onChange={handleFieldChange("note")}
                  rows={2}
                  className="resize-none"
                  placeholder={t("import.agreementAdd.fields.notePlaceholder")}
                />
              </div>
            </div>

            {/* Agreement-level / customer-missing error */}
            {agreementLevelError && (
              <div className="mt-4 flex items-center gap-2 rounded-card bg-red-bg px-4 py-3 text-[13px] text-red">
                <AlertCircle size={16} className="shrink-0" />
                {agreementLevelError}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex justify-end gap-3 border-t border-grey-100 px-4 py-4 sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-control border border-grey-200 px-5 py-2.5 text-[14px] font-medium text-grey-700 hover:bg-grey-50"
            >
              {t("import.agreementAdd.cancelBtn")}
            </button>
            {/* Primary action now opens the contract preview; the real save happens on confirm there. */}
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 rounded-control bg-green px-6 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95 disabled:opacity-60 disabled:active:scale-100"
            >
              {isPending ? (
                <Loader2 size={16} strokeWidth={2} className="animate-spin" />
              ) : (
                <Eye size={16} strokeWidth={2} />
              )}
              {isPending ? t("app.loading") : t("import.agreementAdd.reviewBtn")}
            </button>
          </div>
        </form>

        {previewState && (
          <ContractPreviewModal
            data={previewState.data}
            pdfUrl={previewState.pdfUrl}
            isPending={isDiscarding || isConfirming}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            onDownload={handleDownload}
          />
        )}

        {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      </div>
    </div>,
    document.body,
  );
}
